import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import { Recruit } from "../lib/api";
import { Trash2, Plus, FolderOpen, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await Recruit.listProjects();
      setProjects(data);
    } catch (e) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this hiring project? This action is permanent.")) return;
    try {
      await Recruit.deleteProject(id);
      toast.success("Project deleted");
      load();
    } catch {
      toast.error("Failed to delete project");
    }
  };

  return (
    <div className="min-h-screen bg-canvas" data-testid="dashboard-page">
      <Nav />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow mb-3">Your Console</div>
            <h1 className="display-1 text-4xl lg:text-5xl text-ink">Hiring Projects</h1>
            <p className="text-sub mt-3 max-w-xl">
              Open a project to review candidate matching leaderboards, profiles, and generated interview briefs.
            </p>
          </div>
          <Link 
            to="/app/new" 
            data-testid="new-project-btn" 
            className="btn-accent inline-flex items-center gap-2 self-start"
          >
            <Plus size={16} /> New Hire
          </Link>
        </div>

        {loading ? (
          <div className="text-sub flex items-center gap-2">
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
            <span>Fetching projects...</span>
          </div>
        ) : projects.length === 0 ? (
          <div className="card p-16 text-center" data-testid="empty-projects">
            <div className="w-14 h-14 rounded-2xl bg-haze flex items-center justify-center mx-auto mb-5">
              <FolderOpen size={22} className="text-sub" />
            </div>
            <h2 className="display-2 text-2xl mb-2">No active projects</h2>
            <p className="text-sub mb-6">Get started by creating a new candidate evaluation run.</p>
            <Link 
              to="/app/new" 
              className="btn-accent inline-flex items-center gap-2"
            >
              <Sparkles size={16} /> Start a Hire
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p) => (
              <Link 
                key={p.id} 
                to={`/app/projects/${p.id}`} 
                data-testid={`open-project-${p.id}`}
                className="card card-hover p-7 group relative flex flex-col justify-between min-h-[220px]"
              >
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-xs text-soft uppercase tracking-wider font-medium">
                      {p.role_title || "Open Position"}
                    </span>
                    <button 
                      data-testid={`delete-project-${p.id}`} 
                      onClick={(e) => onDelete(p.id, e)}
                      className="opacity-0 group-hover:opacity-100 transition-all text-soft hover:text-danger p-1"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <h3 className="text-xl font-semibold mt-3 leading-snug tracking-tighter2 text-ink">
                    {p.title}
                  </h3>
                </div>

                <div>
                  <div className="border-t hairline mt-6 pt-5 grid grid-cols-2 gap-4">
                    <div>
                      <div className="eyebrow">Candidates</div>
                      <div className="font-semibold text-2xl mt-0.5 text-ink">{p.candidate_count || 0}</div>
                    </div>
                    <div>
                      <div className="eyebrow">Analyzed</div>
                      <div className="font-semibold text-2xl mt-0.5 text-accent">{p.analyzed_count || 0}</div>
                    </div>
                  </div>
                  <div className="text-xs text-soft mt-5">
                    Created on {p.created_at ? new Date(p.created_at).toLocaleDateString(undefined, {month: "short", day: "numeric", year: "numeric"}) : ""}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
